// 生成随机数
export const random = (min: number, max: number) => Math.random() * (max - min) + min;

interface Position{
  x: number;
  y: number;
}

export type GeneratePosition<T extends object> = T & Position;

export const createPositionGenerator = <T extends object>(
  cardWidth: number,
  cardHeight: number,
  cardMargin = 0,
  screenWidth = window.innerWidth,
  screenHeight = window.innerHeight
) => {
  // 存储已生成的卡片位置
  let existingCards: Position[] = [];

  // 计算两个矩形重叠的面积
  const calculateOverlapArea = (
    x1: number,
    y1: number,
    x2: number,
    y2: number
  ) => {
    const overlapX = Math.max(0, Math.min(x1 + cardWidth, x2 + cardWidth) - Math.max(x1, x2));
    const overlapY = Math.max(0, Math.min(y1 + cardHeight, y2 + cardHeight) - Math.max(y1, y2));
    return overlapX * overlapY;
  };

  // 计算位置的空闲程度（只考虑重叠面积）
  const calculateSpaceScore = (
    x: number,
    y: number
  ) => {
    if (existingCards.length === 0) return 0;
    
    let totalOverlap = 0;
    
    existingCards.forEach(card => {
      // 计算重叠面积
      const overlap = calculateOverlapArea(x, y, card.x, card.y);
      totalOverlap += overlap;
    });
    
    // 返回负的重叠面积，这样重叠面积越小，得分越高
    return -totalOverlap;
  };

  // 生成单个卡片的位置
  const generatePosition = (index: number):Position => {
    let attempts = 0;
    const maxAttempts = 200; // 增加尝试次数以找到更好的位置
    let bestPosition = { x: 0, y: 0 };
    let bestScore = -Infinity;

    do {
      const x = random(cardMargin, screenWidth - cardWidth - cardMargin);
      const y = random(cardMargin, screenHeight - cardHeight - cardMargin);
      
      // 检查是否在屏幕范围内
      if (
        x < cardMargin ||
        y < cardMargin ||
        x + cardWidth > screenWidth - cardMargin ||
        y + cardHeight > screenHeight - cardMargin
      ) {
        continue;
      }
      
      // 计算这个位置的得分
      const score = calculateSpaceScore(x, y);
      if (score > bestScore) {
        bestScore = score;
        bestPosition = { x, y };
      }
      
      attempts++;
    } while (attempts < maxAttempts);

    // 将新位置添加到已存在的位置列表中
    existingCards.push(bestPosition);
    
    return bestPosition;
  };

  // 返回处理整个卡片列表的函数
  return (cards: T[]):GeneratePosition<T>[] => {
    existingCards = [];
    return cards.map((card, index) => ({
      ...card,
      ...generatePosition(index)
    }));
  };
};